import React from 'react';
import { MessageCircle } from 'lucide-react';
import { Click2CallTrigger } from '@/components/ui/click-2-call-trigger';
import { cn } from '@/lib/utils';

/** Format phone for WhatsApp: digits only (country code + number) */
function formatPhoneForWhatsApp(phone: string): string {
    return phone.replace(/\D/g, '');
}

interface ConditionalPhoneFieldProps {
    phoneNumber: string;
    customerId?: string;
    className?: string;
    /** When true and VOIP + user extension (for Arestik) are ready, show click-to-call trigger with confirm dialog */
    showCallButton?: boolean;
}

/**
 * Renders a phone value. When VOIP is enabled and user has extension when required (Arestik),
 * shows the Click2CallTrigger: value + phone icon + WhatsApp icon. Click-to-call has confirm dialog;
 * WhatsApp opens wa.me/... in a new tab (no confirmation).
 */
export const ConditionalPhoneField: React.FC<ConditionalPhoneFieldProps> = ({
    phoneNumber,
    customerId,
    className,
    showCallButton = true,
}) => {
    const hasPhone = Boolean(phoneNumber?.trim());
    const whatsappHref = hasPhone
        ? `https://wa.me/${formatPhoneForWhatsApp(phoneNumber)}`
        : undefined;

    if (showCallButton) {
        return (
            <Click2CallTrigger
                value={phoneNumber}
                customerId={customerId}
                className={className}
                disabled={false}
            />
        );
    }

    // Plain phone number + WhatsApp when call button is explicitly hidden
    return (
        <div className={cn('flex items-center gap-1', className)}>
            <span className="font-mono">{phoneNumber || '—'}</span>
            {hasPhone && (
                <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Open WhatsApp"
                    aria-label={`Open WhatsApp chat with ${phoneNumber}`}
                >
                    <MessageCircle className="h-3.5 w-3.5" />
                </a>
            )}
        </div>
    );
};
